import React from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TablePaginationActions from "../../../../../globals/components/TablePaginationActions";

function LawFirmLocationsTabPanel({
  id,
  navigate,
  locationSearchQuery,
  handleLocationSearch,
  clearLocationSearch,
  filteredLocations,
  locations,
  paginatedLocations,
  locationsPage,
  locationsRowsPerPage,
  handleLocationsPageChange,
  handleLocationsRowsPerPageChange,
  handleDeleteLocationClick,
}) {
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 3,
          backgroundColor: "#e3f2fd", // Light blue background
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "600",
            color: "#1976d2", // Blue text
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <LocationOnIcon />
          Manage Locations
        </Typography>
        <Button
          onClick={() => navigate(`/add_location/${id}`)}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          sx={{ borderRadius: "20px" }}
        >
          Add New Location
        </Button>
      </Box>

      {/* Search Field for Locations */}
      <Box
        sx={{
          p: 3,
          backgroundColor: "#f8f9fa",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search locations by name..."
          value={locationSearchQuery}
          onChange={handleLocationSearch}
          slotProps={{ input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#1976d2" }} />
              </InputAdornment>
            ),
            endAdornment: locationSearchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={clearLocationSearch}
                  sx={{ color: "#1976d2" }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1976d2",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1976d2",
              },
            },
          }}
        />
        {locationSearchQuery && (
          <Typography variant="body2" sx={{ mt: 1, color: "#1976d2" }}>
            Showing {filteredLocations.length} of {locations.length} locations
          </Typography>
        )}
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                Location Name
              </TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                Customer #
              </TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                Address
              </TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                City
              </TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                County
              </TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                State
              </TableCell>
              <TableCell
                sx={{ fontWeight: "600", fontSize: "0.9rem" }}
                align="center"
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLocations.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  sx={{ py: 6, color: "text.secondary" }}
                >
                  {locationSearchQuery ? (
                    <>
                      <SearchIcon
                        sx={{
                          fontSize: 64,
                          mb: 2,
                          opacity: 0.3,
                          color: "#1976d2",
                        }}
                      />
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        No locations found
                      </Typography>
                      <Typography variant="body2">
                        Try adjusting your search terms or{" "}
                        <Button
                          onClick={clearLocationSearch}
                          sx={{
                            textTransform: "none",
                            p: 0,
                            minWidth: "auto",
                          }}
                        >
                          clear search
                        </Button>
                      </Typography>
                    </>
                  ) : (
                    <>
                      <LocationOnIcon
                        sx={{
                          fontSize: 64,
                          mb: 2,
                          opacity: 0.3,
                          color: "#1976d2",
                        }}
                      />
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        No locations added yet
                      </Typography>
                      <Typography variant="body2">
                        Click "Add New Location" to get started
                      </Typography>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              paginatedLocations.map((location) => (
                <TableRow
                  key={location.law_firm_location_id}
                  hover
                  sx={{
                    "&:hover": {
                      backgroundColor: "#f8f9fa",
                    },
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <TableCell
                    sx={{ fontSize: "0.875rem", fontWeight: "500" }}
                  >
                    {location.name}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem" }}>
                    <Chip
                      label={location.customer_number || "N/A"}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem" }}>
                    {location.address || "N/A"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem" }}>
                    {location.city || "N/A"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem" }}>
                    {location.county || "N/A"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem" }}>
                    {location.state || "N/A"}
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "center",
                      }}
                    >
                      <Tooltip title="Edit Location">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() =>
                            navigate(
                              `/lawfirmlocationdetails/${location.law_firm_location_id}`
                            )
                          }
                          sx={{
                            "&:hover": {
                              backgroundColor: "#1976d2",
                              color: "white",
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Location">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteLocationClick(location)}
                          sx={{
                            "&:hover": {
                              backgroundColor: "error.main",
                              color: "white",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {filteredLocations.length > 0 && (
        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={filteredLocations.length}
          page={locationsPage}
          onPageChange={handleLocationsPageChange}
          rowsPerPage={locationsRowsPerPage}
          onRowsPerPageChange={handleLocationsRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            backgroundColor: "#f8f9fa",
            borderTop: "1px solid #e0e0e0",
            "& .MuiTablePagination-toolbar": {
              color: "#1976d2",
            },
            "& .MuiTablePagination-selectIcon": {
              color: "#1976d2",
            },
          }}
        />
      )}
    </Box>
  );
}

export default LawFirmLocationsTabPanel;
